import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import ReactPagination from 'react-js-pagination';

// actions
import { getAgent } from '../../actions/agent';

// components
import { Table } from '../../common/components';

function Pagination({ handleLoadAgents }) {
  const dispatch = useDispatch();
  const {
    activePage,
    pageSize,
    totalPages,
    totalAgents,
    agentBook,
  } = useSelector(store => store.agents);

  const pageCountStart = ((activePage - 1) * pageSize) + 1;
  let pageCountEnd = activePage * pageSize;
  if (totalPages === activePage) pageCountEnd = totalAgents;

  return (
    <div className="d-flex flex-row m-3 position-relative">
      <div className="d-flex ms-0 position-absolute">
        <span className="text-muted">
          <i>{`${pageCountStart}-${pageCountEnd} of ${totalAgents} agents`}</i>
        </span>
      </div>
      <ReactPagination
        activePage={activePage}
        itemsCountPerPage={pageSize}
        totalItemsCount={totalAgents}
        pageRangeDisplayed={5}
        innerClass="pagination mb-0 ms-auto me-auto"
        itemClass="page-item"
        linkClass="page-link"
        onChange={(pageNum) => {
          if (agentBook[pageNum - 1]) {
            dispatch({ type: 'SET_ACTIVE_PAGE', payload: pageNum });
          } else {
            handleLoadAgents({ page: pageNum });
          }
        }}
      />
    </div>
  );
}

function LoadDetailsButton({ username }) {
  const dispatch = useDispatch();
  const [ loading, setLoading ] = useState(false);

  function handleLoadAgent(un) {
    setLoading(true);
    getAgent(un)
      .then(({ data }) => {
        dispatch({ type: 'SET_SELECTED_AGENT', agent: data });
        dispatch({ type: 'OPEN_EDIT_INFO', username: un });
      })
      .finally(() => setLoading(false));
  }

  return (
    <button
      type="button"
      className="btn btn-link p-0"
      style={{ width: '40px' }}
      onClick={() => handleLoadAgent(username)}
    >
      {loading ? 'Loading...' : 'Details'}
    </button>
  );
}

function AccountTypeBadge({ data }) {
  const {
    is_agent: isAgent,
    is_admin: isAdmin,
    is_agency_admin: isGroupAdmin,
    id,
  } = data;
  const returnBadges = [];
  if (isAdmin) returnBadges.push(<span className="badge rounded-pill text-bg-success me-1" key={`${id}-admin`}>Admin</span>);
  if (isGroupAdmin) returnBadges.push(<span className="badge text-white rounded-pill text-bg-primary me-1" key={`${id}-group-admin`}>Group</span>);
  if (isAgent) returnBadges.push(<span className="badge rounded-pill text-bg-secondary" key={`${id}-agent`}>Agent</span>);
  return returnBadges;
}

function UserTable({ handleLoadAgents, loading, errorMessage }) {
  const dispatch = useDispatch();
  const {
    agentPage: agents,
    sortInverse: sortDirection,
    sortTerm,
  } = useSelector(store => store.agents);
  const { npnAsSAN, agentRegistration } = useSelector(store => store.theme.features);

  // cell formatting
  const formatNameCell = (data) => `${data.first_name}${data.last_name ? ` ${data.last_name}` : ''}`;
  const formatDateCell = (date) => (date ? moment(date).format('MM/DD/YYYY') : <span className="text-muted">--</span>);
  const formatDetailsCell = (username) => <LoadDetailsButton username={username} />;
  const formatRegisteredCell = (date) => {
    if (date) return moment(date).format('MM/DD/YYYY');
    return <span className="text-danger">Not registered</span>;
  };
  const formatAccountType = (data) => <AccountTypeBadge data={data} />;

  // table schema
  const npnHeaderName = (npnAsSAN ? 'SAN' : 'NPN');
  const schema = {
    cols: [
      {
        header: 'Name',
        passAllData: true,
        formatter: formatNameCell,
        sortKey: 'last_name',
        alphaSortIcon: true,
        defaultSortOrder: 'asc',
      },
      {
        header: npnHeaderName,
        field: 'npn',
        sortKey: 'npn',
      },
      {
        header: 'Email',
        field: 'email',
        sortKey: 'email',
        alphaSortIcon: true,
        defaultSortOrder: 'asc',
      },
      {
        header: 'Type',
        passAllData: true,
        formatter: formatAccountType,
      },
      {
        header: 'Last Update',
        field: 'last_updated',
        formatter: formatDateCell,
        sortKey: 'last_updated',
      },
      {
        header: 'Created',
        field: 'created',
        formatter: formatDateCell,
        sortKey: 'created',
      },
      (agentRegistration ? {
        header: 'Registered',
        field: 'registered_date',
        formatter: formatRegisteredCell,
        sortKey: 'registered_date',
      } : null),
      {
        header: 'Last Login',
        field: 'last_login_date',
        formatter: formatDateCell,
        sortKey: 'last_login_date',
        cellStyle: { textAlign: 'center' },
      },
      {
        header: 'Details',
        field: 'username',
        formatter: formatDetailsCell,
      },
    ],
    sortClickCallback: (key, direction) => handleLoadAgents({ key, direction }),
    sortKeySetter: (sk) => dispatch({ type: 'SET_AGENT_SORT_TERM', payload: sk }),
    sortKeyGetter: () => sortTerm,
    sortDirectionSetter: (sd) => dispatch({ type: 'SET_AGENT_SORT_INVERSE', payload: sd }),
    sortDirectionGetter: () => sortDirection,
  };

  return (
    <>
      <Pagination handleLoadAgents={handleLoadAgents} />
      <div className="overflow-scroll">
        <Table
          className="table table-striped"
          schema={schema}
          data={agents}
          loading={loading}
          errorMessage={errorMessage}
        />
      </div>
      <Pagination handleLoadAgents={handleLoadAgents} />
    </>
  );
}

export default UserTable;
